import React from "react";
import { Redirect } from "react-router-dom";
import MeasuresList from "../components/MeasuresList";
import UpdatedData from "../components/UpdatedData";
import PortsList from "../components/PortList";
import axios from "../util/Api";
import getStorage from "../context/auth";
import { tokensKey } from "../util/Constants";
import { Select, message, Spin } from "antd";
import { normalizeMembersForPayload, memberPortOptions } from "../util/ClientMembers";
import "antd/dist/antd.css";

const { Option } = Select;

class Profile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sectionNumber: 0,
      loggedOut: false,
      user: null,
      profileLoadError: null,
      memberUnderEditing: null,
      selectedPorts: null,
      isUpdatingMembers: false,
    };
  }

  componentDidMount() {
    this.profileMounted = true;
    this.getProfile();
  }

  componentWillUnmount() {
    this.profileMounted = false;
  }

  getProfile = () => {
    this.setState({ profileLoadError: null });
    const stored = getStorage().getItem(tokensKey);
    if (!stored) {
      this.setState({ loggedOut: true });
      return;
    }
    const { jwt } = JSON.parse(stored);


    return axios.get(`/api/mesures/full-me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then((res) => {
        if (!this.profileMounted) return;
        if (res.status === 200) {
          this.setState({ user: res.data });
        } else {
          console.warn('⚠️  Unexpected status:', res.status);
          this.onLogout();
        }
      })
      .catch((err) => {
        if (!this.profileMounted) return;
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          this.onLogout();
        } else {
          this.setState({ profileLoadError: "Impossible de charger le profil. Réessayez." });
        }
      });
  };

  handleMemberChange = (value) => {
    this.setState({ selectedPorts: value });
  };

  applyMemberResponse = async (response) => {
    const members = response?.data?.client_members ?? response?.data?.data?.client_member;
    if (!this.profileMounted) return;
    if (Array.isArray(members)) {
      this.setState(({ user }) => ({
        user: { ...user, client_member: members },
        memberUnderEditing: null,
        selectedPorts: [],
      }));
    } else {
      // Compatibility with older backend responses while releases are rolled out.
      await this.getProfile();
      if (this.profileMounted) this.setState({ memberUnderEditing: null, selectedPorts: [] });
    }
  };

  handleAvatarError = (event) => {
    if (!event?.target) return;
    event.target.onerror = null; // prevent infinite loop
    event.target.src = "images/client/office.png";
  };

  validMemberChanges = async () => {
    const {
      user,
      memberUnderEditing,
      selectedPorts,
    } = this.state;
    if (!user || !memberUnderEditing || this.state.isUpdatingMembers) return;

    // 1. Make a safe copy
    const currentMembers = [...(user.client_member || [])];

    // 2. Find the member we’re editing
    const idx = currentMembers.findIndex(
      m => m.id === memberUnderEditing.id
    );
    if (idx < 0) throw new Error("Member not found");

    // 3. Overwrite *only* their ports with an array of IDs
    currentMembers[idx] = {
      ...currentMembers[idx],
      ports: selectedPorts || []
    };

    // 4. Push to Strapi
    const { jwt } = JSON.parse(getStorage().getItem(tokensKey));
    this.setState({ isUpdatingMembers: true });
    try {
      const response = await axios.put(
        `/api/mesures/update-user`,
        { client_member: normalizeMembersForPayload(currentMembers) },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      await this.applyMemberResponse(response);
      if (this.profileMounted) message.success("Membre mis à jour");
    } catch (error) {
      if (!this.profileMounted) return;
      if (error?.response?.status === 401) return this.onLogout();
      message.error("Impossible de sauvegarder les modifications");
    } finally {
      if (this.profileMounted) this.setState({ isUpdatingMembers: false });
    }
  };

  handleRemoveMember = async (member) => {
    if (!member || this.state.isUpdatingMembers) return;
    if (
      !window.confirm(
        `Supprimer le membre ${member.member_email || member.member_name || ""
        } ?`
      )
    ) {
      return;
    }

    const { user } = this.state;
    if (!user) return;

    const updatedMembers = (user.client_member || []).filter(
      (m) =>
        (m.member_email || "").toLowerCase() !==
        (member.member_email || "").toLowerCase()
    );

    const { jwt } = JSON.parse(getStorage().getItem(tokensKey));
    this.setState({ isUpdatingMembers: true });
    try {
      const response = await axios.put(
        `/api/mesures/update-user`,
        { client_member: normalizeMembersForPayload(updatedMembers) },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      await this.applyMemberResponse(response);
      if (this.profileMounted) message.success("Membre supprimé");
    } catch (error) {
      if (!this.profileMounted) return;
      if (error?.response?.status === 401) return this.onLogout();
      message.error("Impossible de supprimer ce membre");
    } finally {
      if (this.profileMounted) this.setState({ isUpdatingMembers: false });
    }
  };

  onLogout = () => {
    getStorage().removeItem(tokensKey);
    this.setState({ loggedOut: true });
    if (this.props.updateLoginState) {
      this.props.updateLoginState();
    }
  };

  render() {
    const {
      sectionNumber,
      user,
      profileLoadError,
      memberUnderEditing,
      loggedOut,
      isUpdatingMembers,
      selectedPorts,
    } = this.state;

    if (loggedOut) {
      return <Redirect to="/login" />;
    }

    if (!user) {
      return profileLoadError ? (
        <div className="container py-5" role="alert">
          <div className="alert alert-warning">{profileLoadError}</div>
          <button type="button" className="btn btn-primary" onClick={this.getProfile}>Réessayer</button>
        </div>
      ) : <div style={{ padding: 20 }}>Chargement...</div>;
    }

    const imageUrl = user.icon
      ? user.icon.formats?.thumbnail?.url || user.icon.url
      : "images/client/office.png";

    const coverUrl = user.cover
      ? user.cover.formats?.large?.url || user.cover.url
      : "images/account/bg.png";

    return (
      <div>
        <section
          className="bg-profile d-table w-100 bg-primary"
          style={{ background: `url("${coverUrl}") center center` }}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="card public-profile border-0 rounded shadow"
                  style={{ zIndex: 1 }}
                >
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-lg-2 col-md-3 text-md-left text-center">
                        <img
                          src={imageUrl}
                          className="avatar shadow d-block mx-auto"
                          alt=""
                          height={130}
                          width={130}
                          onError={this.handleAvatarError}
                          loading="lazy"
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                      <div className="col-lg-10 col-md-9">
                        <div className="row align-items-end">
                          <div className="col-md-7 text-md-left text-center mt-4 mt-sm-0">
                            <h3 className="title mb-0">{user.username}</h3>
                            <small className="text-muted h6 mr-2">
                              {user.email}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section mt-60">
          <div className="container mt-lg-3">
            <div className="row">
              <div className="col-lg-4 col-md-6 col-12 d-lg-block">
                <div className="sidebar sticky-bar p-4 rounded shadow">
                  <div className="widget mt-4 pt-2">
                    <h5 className="widget-title">Détails :</h5>
                  </div>
                  <div className="widget">
                    <div className="row">
                      {[
                        { num: 0, icon: "uil-dashboard", label: "Profile" },
                        { num: 1, icon: "uil-hunting", label: "Ports" },
                        { num: 2, icon: "uil-users-alt", label: "Membres" },
                        { num: 3, icon: "uil-file", label: "Jaugeage" },
                      ].map((item) => (
                        <div
                          key={item.num}
                          className="col-6 mt-4 pt-2"
                          onClick={() => this.setState({ sectionNumber: item.num })}
                        >
                          <div
                            className={`accounts rounded d-block shadow text-center py-3 ${sectionNumber === item.num ? "active" : ""
                              }`}
                          >
                            <span className="pro-icons h3 text-muted">
                              <i className={`uil ${item.icon}`} />
                            </span>
                            <h6 className="title text-dark h6 my-0">{item.label}</h6>
                          </div>
                        </div>
                      ))}
                      <div className="col-6 mt-4 pt-2">
                        <div
                          className="accounts rounded d-block shadow text-center py-3"
                          onClick={this.onLogout}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="pro-icons h3 text-muted">
                            <i className="uil uil-sign-out-alt" />
                          </span>
                          <h6 className="title text-dark h6 my-0">Déconnecter</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-8 col-12">
                <div className="border-bottom pb-4">
                  {sectionNumber === 0 && <UpdatedData updatedUser={user} />}
                  {sectionNumber === 1 && <PortsList ports={user.ports} />}
                  {sectionNumber === 2 && (
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="table-responsive bg-white shadow rounded p-4">
                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                            <h5 className="mb-3 mb-md-0">Membres :</h5>
                            <div className="d-flex align-items-center">
                              {isUpdatingMembers && <Spin size="small" className="mr-3" />}
                            </div>
                          </div>
                          <div className="row">
                            {(user.client_member || []).length === 0 && (
                              <div className="col-12 mt-4">
                                <div className="alert alert-light mb-0" role="alert">
                                  Aucun membre n'est associé à votre compte.
                                </div>
                              </div>
                            )}
                            {user.client_member?.map((element, idx) => (
                              <div
                                className="col-lg-12 col-md-12 col-12 mt-4 pt-2"
                                key={element.member_email || idx}
                              >
                                <div className="media key-feature align-items-center p-3 rounded shadow-sm">
                                  <div className="media-body ml-3">
                                    <h4 className="title mb-0 text-dark d-flex flex-wrap align-items-center">
                                      <span>{element.member_email || element.member_name || "Membre"}</span>
                                      <span
                                        onClick={() => {
                                          if (memberUnderEditing === element) {
                                            this.validMemberChanges();
                                          } else {
                                            this.setState({
                                              memberUnderEditing: element,
                                              selectedPorts: Array.isArray(element.ports)
                                                ? element.ports.map((el) => el.id)
                                                : [],
                                            });
                                          }
                                        }}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <span className="badge badge-pill badge-info ml-2">
                                          {memberUnderEditing === element
                                            ? "Sauvegarder"
                                            : "Modifier"}
                                        </span>
                                      </span>
                                      {memberUnderEditing === element && (
                                        <span
                                          className="badge badge-pill badge-secondary ml-2"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            this.setState({ memberUnderEditing: null, selectedPorts: [] })
                                          }
                                        >
                                          Annuler
                                        </span>
                                      )}
                                      <span
                                        className="badge badge-pill badge-danger ml-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => this.handleRemoveMember(element)}
                                      >
                                        Supprimer
                                      </span>
                                    </h4>

                                    {memberUnderEditing === element ? (
                                      <Select
                                        mode="multiple"
                                        placeholder="Ports à occuper"
                                        value={selectedPorts || []}
                                        onChange={this.handleMemberChange}
                                        style={{ width: "100%" }}
                                        disabled={isUpdatingMembers}
                                        optionFilterProp="children"
                                      >
                                        {memberPortOptions(user, element).map((port) => (
                                            <Option key={port.id} value={port.id}>
                                              {port.name}
                                            </Option>
                                          ))}
                                      </Select>
                                    ) : (
                                      (element.ports || []).map((portElement, i) => (
                                        <h6 className="text-primary mb-0" key={i}>
                                          {portElement.name}
                                        </h6>
                                      ))
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {sectionNumber === 3 && <MeasuresList updatedUser={user} />}
                </div>
              </div>
            </div>
          </div>
        </section >
      </div >
    );
  }
}

export default Profile;
